import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ChevronLeftIcon, SearchIcon, PlusIcon, XIcon, CheckIcon, LayoutIcon, StarIcon, CalendarIcon, DollarSignIcon, MapPinIcon, UsersIcon, ShieldIcon, ClockIcon, ArrowRightIcon } from 'lucide-react';
// Mock data for tour packages
const tourPackages = [{
  id: 1,
  destination: 'Bali, Indonesia',
  image: 'https://images.unsplash.com/photo-1537996194471-e657df975ab4?ixlib=rb-1.2.1&auto=format&fit=crop&w=800&q=80',
  title: 'Tropical Paradise Retreat',
  duration: '7 days, 6 nights',
  price: 1299,
  availability: 'available',
  rating: 4.8,
  category: 'beach',
  description: 'Experience the ultimate tropical getaway in Bali.',
  features: {
    accommodation: '4-star beachfront resort',
    meals: 'Breakfast included',
    transfers: 'Shared airport transfers',
    activities: ['Temple tours', 'Snorkeling excursion', 'Spa treatment'],
    guide: 'English-speaking guide for tours',
    flights: 'Not included'
  }
}, {
  id: 2,
  destination: 'Rome, Italy',
  image: 'https://images.unsplash.com/photo-1552832230-c0197dd311b5?ixlib=rb-1.2.1&auto=format&fit=crop&w=800&q=80',
  title: 'Roman History Explorer',
  duration: '6 days, 5 nights',
  price: 1599,
  availability: 'limited',
  rating: 4.9,
  category: 'culture',
  description: 'Discover the ancient wonders and modern delights of Rome.',
  features: {
    accommodation: '4-star central hotel',
    meals: 'Breakfast included',
    transfers: 'Private airport transfers',
    activities: ['Colosseum tour', 'Vatican City visit', 'Food tour'],
    guide: 'Expert historian guide',
    flights: 'Not included'
  }
}, {
  id: 3,
  destination: 'Kyoto, Japan',
  image: 'https://images.unsplash.com/photo-1493976040374-85c8e12f0c0e?ixlib=rb-1.2.1&auto=format&fit=crop&w=800&q=80',
  title: 'Traditional Japan Experience',
  duration: '8 days, 7 nights',
  price: 2199,
  availability: 'available',
  rating: 4.7,
  category: 'culture',
  description: 'Immerse yourself in Japanese culture and traditions.',
  features: {
    accommodation: 'Mix of ryokan and 4-star hotels',
    meals: 'Breakfast and 3 dinners included',
    transfers: 'Private airport transfers',
    activities: ['Tea ceremony', 'Temple visits', 'Cooking class'],
    guide: 'English-speaking local guide',
    flights: 'Not included'
  }
}, {
  id: 4,
  destination: 'Swiss Alps',
  image: 'https://images.unsplash.com/photo-1491555103944-7c647fd857e6?ixlib=rb-1.2.1&auto=format&fit=crop&w=800&q=80',
  title: 'Alpine Adventure',
  duration: '5 days, 4 nights',
  price: 1899,
  availability: 'available',
  rating: 4.8,
  category: 'adventure',
  description: 'Experience the breathtaking beauty of the Swiss Alps.',
  features: {
    accommodation: '4-star mountain lodge',
    meals: 'Half-board (breakfast and dinner)',
    transfers: 'Train station transfers',
    activities: ['Hiking', 'Cable car rides', 'Cheese tasting'],
    guide: 'Mountain guide for activities',
    flights: 'Not included'
  }
}, {
  id: 5,
  destination: 'Santorini, Greece',
  image: 'https://images.unsplash.com/photo-1570077188670-e3a8d69ac5ff?ixlib=rb-1.2.1&auto=format&fit=crop&w=800&q=80',
  title: 'Aegean Island Escape',
  duration: '6 days, 5 nights',
  price: 1799,
  availability: 'limited',
  rating: 4.9,
  category: 'beach',
  description: 'Relax on the stunning beaches of Santorini with breathtaking views.',
  features: {
    accommodation: '4-star cliffside hotel',
    meals: 'Breakfast included',
    transfers: 'Private airport transfers',
    activities: ['Sunset cruise', 'Wine tasting', 'Beach day'],
    guide: 'Local guide for select activities',
    flights: 'Not included'
  }
}, {
  id: 6,
  destination: 'Machu Picchu, Peru',
  image: 'https://images.unsplash.com/photo-1526392060635-9d6019884377?ixlib=rb-1.2.1&auto=format&fit=crop&w=800&q=80',
  title: 'Inca Trail Expedition',
  duration: '9 days, 8 nights',
  price: 2499,
  availability: 'limited',
  rating: 4.9,
  category: 'adventure',
  description: 'Trek the famous Inca Trail to Machu Picchu.',
  features: {
    accommodation: 'Mix of 3-star hotels and camping',
    meals: 'All meals during trek, breakfast at hotels',
    transfers: 'All ground transportation included',
    activities: ['Inca Trail trek', 'Cusco city tour', 'Sacred Valley visit'],
    guide: 'Expert trekking guide',
    flights: 'Not included'
  }
}];
const featureCategories = [{
  id: 'price',
  label: 'Price'
}, {
  id: 'duration',
  label: 'Duration'
}, {
  id: 'accommodation',
  label: 'Accommodation'
}, {
  id: 'meals',
  label: 'Meals'
}, {
  id: 'transfers',
  label: 'Transfers'
}, {
  id: 'activities',
  label: 'Activities'
}, {
  id: 'guide',
  label: 'Guide'
}, {
  id: 'flights',
  label: 'Flights'
}];
export const ComparisonTool = () => {
  const navigate = useNavigate();
  const [selectedTours, setSelectedTours] = useState<any[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [showTourSelector, setShowTourSelector] = useState(false);
  const [filteredTours, setFilteredTours] = useState(tourPackages);
  useEffect(() => {
    if (searchTerm) {
      const filtered = tourPackages.filter(tour => tour.title.toLowerCase().includes(searchTerm.toLowerCase()) || tour.destination.toLowerCase().includes(searchTerm.toLowerCase()));
      setFilteredTours(filtered);
    } else {
      setFilteredTours(tourPackages);
    }
  }, [searchTerm]);
  const addTourToComparison = (tour: any) => {
    if (selectedTours.length < 3 && !selectedTours.some(t => t.id === tour.id)) {
      setSelectedTours([...selectedTours, tour]);
      setShowTourSelector(false);
      setSearchTerm('');
    }
  };
  const removeTourFromComparison = (tourId: number) => {
    setSelectedTours(selectedTours.filter(tour => tour.id !== tourId));
  };
  const handleViewDetails = (tourId: number) => {
    navigate(`/products/${tourId}`);
  };
  const renderFeatureValue = (tour: any, featureId: string) => {
    if (featureId === 'price') {
      return <span className="font-bold text-blue-600" data-id="element-1015">${tour.price}</span>;
    }
    if (featureId === 'duration') {
      return tour.duration;
    }
    if (Array.isArray(tour.features[featureId])) {
      return <ul className="list-disc pl-5 text-sm" data-id="element-1016">
          {tour.features[featureId].map((item: string, idx: number) => <li key={idx} data-id="element-1017">{item}</li>)}
        </ul>;
    }
    return tour.features[featureId];
  };
  const renderFeatureComparison = (featureId: string, label: string) => {
    return <tr className="border-b border-gray-200" data-id="element-1018">
        <td className="py-3 px-4 bg-gray-50 font-medium" data-id="element-1019">{label}</td>
        {selectedTours.map(tour => <td key={tour.id} className="py-3 px-4" data-id="element-1020">
            {renderFeatureValue(tour, featureId)}
          </td>)}
        {[...Array(3 - selectedTours.length)].map((_, i) => <td key={i} className="py-3 px-4 bg-gray-50" data-id="element-1021"></td>)}
      </tr>;
  };
  return <div className="space-y-6" data-id="element-1022">
      <div className="flex justify-between items-center" data-id="element-1023">
        <h1 className="text-2xl font-bold text-gray-800" data-id="element-1024">Tour Comparison</h1>
        <button className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700" onClick={() => setShowTourSelector(true)} data-id="element-1025">
          Add Tour to Compare
        </button>
      </div>
      {/* Tour selection header */}
      <div className="flex mb-6 space-x-4" data-id="element-1026">
        {selectedTours.map(tour => <div key={tour.id} className="flex-1 bg-white rounded-lg shadow-md overflow-hidden relative" data-id="element-1027">
            <button className="absolute top-2 right-2 p-1 bg-white rounded-full shadow-sm" onClick={() => removeTourFromComparison(tour.id)} data-id="element-1028">
              <XIcon className="h-4 w-4 text-gray-500" data-id="element-1029" />
            </button>
            <img src={tour.image} alt={tour.title} className="w-full h-32 object-cover" data-id="element-1030" />
            <div className="p-3" data-id="element-1031">
              <h3 className="font-medium text-gray-800 line-clamp-1" data-id="element-1032">
                {tour.title}
              </h3>
              <p className="text-sm text-gray-600" data-id="element-1033">{tour.destination}</p>
            </div>
          </div>)}
        {selectedTours.length < 3 && <div className="flex-1 border-2 border-dashed border-gray-300 rounded-lg flex items-center justify-center cursor-pointer hover:bg-gray-50" onClick={() => setShowTourSelector(true)} data-id="element-1034">
            <div className="text-center p-4" data-id="element-1035">
              <div className="w-12 h-12 rounded-full bg-blue-100 flex items-center justify-center mx-auto mb-2" data-id="element-1036">
                <PlusIcon className="h-6 w-6 text-blue-600" data-id="element-1037" />
              </div>
              <p className="text-sm font-medium text-blue-600" data-id="element-1038">
                Add Tour to Compare
              </p>
            </div>
          </div>}
      </div>
      {selectedTours.length === 0 ? <div className="bg-white rounded-lg shadow-md p-8 text-center" data-id="element-1039">
          <div className="w-16 h-16 rounded-full bg-blue-100 flex items-center justify-center mx-auto mb-4" data-id="element-1040">
            <LayoutIcon className="h-8 w-8 text-blue-600" data-id="element-1041" />
          </div>
          <h2 className="text-xl font-medium text-gray-800 mb-2" data-id="element-1042">
            Start Comparing Tours
          </h2>
          <p className="text-gray-600 mb-6 max-w-md mx-auto" data-id="element-1043">
            Add up to 3 tour packages to compare their features, prices, and
            inclusions side by side.
          </p>
          <button className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors" onClick={() => setShowTourSelector(true)} data-id="element-1044">
            Add Tours to Compare
          </button>
        </div> : <div className="bg-white rounded-lg shadow-md overflow-hidden" data-id="element-1045">
          <table className="w-full border-collapse" data-id="element-1046">
            <thead data-id="element-1047">
              <tr className="bg-gray-50 border-b border-gray-200" data-id="element-1048">
                <th className="py-3 px-4 text-left text-gray-600 font-medium" data-id="element-1049">
                  Feature
                </th>
                {selectedTours.map(tour => <th key={tour.id} className="py-3 px-4 text-left text-gray-800 font-medium" data-id="element-1050">
                    {tour.title}
                  </th>)}
                {[...Array(3 - selectedTours.length)].map((_, i) => <th key={i} className="py-3 px-4 bg-gray-50" data-id="element-1051"></th>)}
              </tr>
            </thead>
            <tbody data-id="element-1052">
              <tr className="border-b border-gray-200" data-id="element-1053">
                <td className="py-3 px-4 bg-gray-50 font-medium" data-id="element-1054">
                  Destination
                </td>
                {selectedTours.map(tour => <td key={tour.id} className="py-3 px-4" data-id="element-1055">
                    <div className="flex items-center" data-id="element-1056">
                      <MapPinIcon className="h-4 w-4 text-gray-400 mr-1" data-id="element-1057" />
                      {tour.destination}
                    </div>
                  </td>)}
                {[...Array(3 - selectedTours.length)].map((_, i) => <td key={i} className="py-3 px-4 bg-gray-50" data-id="element-1058"></td>)}
              </tr>
              <tr className="border-b border-gray-200" data-id="element-1059">
                <td className="py-3 px-4 bg-gray-50 font-medium" data-id="element-1060">Rating</td>
                {selectedTours.map(tour => <td key={tour.id} className="py-3 px-4" data-id="element-1061">
                    <div className="flex items-center" data-id="element-1062">
                      <StarIcon className="h-4 w-4 text-yellow-400 mr-1" data-id="element-1063" />
                      <span data-id="element-1064">{tour.rating}/5</span>
                    </div>
                  </td>)}
                {[...Array(3 - selectedTours.length)].map((_, i) => <td key={i} className="py-3 px-4 bg-gray-50" data-id="element-1065"></td>)}
              </tr>
              <tr className="border-b border-gray-200" data-id="element-1066">
                <td className="py-3 px-4 bg-gray-50 font-medium" data-id="element-1067">Duration</td>
                {selectedTours.map(tour => <td key={tour.id} className="py-3 px-4" data-id="element-1068">
                    <div className="flex items-center" data-id="element-1069">
                      <ClockIcon className="h-4 w-4 text-gray-400 mr-1" data-id="element-1070" />
                      {tour.duration}
                    </div>
                  </td>)}
                {[...Array(3 - selectedTours.length)].map((_, i) => <td key={i} className="py-3 px-4 bg-gray-50" data-id="element-1071"></td>)}
              </tr>
              <tr className="border-b border-gray-200" data-id="element-1072">
                <td className="py-3 px-4 bg-gray-50 font-medium" data-id="element-1073">Price</td>
                {selectedTours.map(tour => <td key={tour.id} className="py-3 px-4" data-id="element-1074">
                    <div className="flex items-center" data-id="element-1075">
                      <DollarSignIcon className="h-4 w-4 text-gray-400 mr-1" data-id="element-1076" />
                      <span className="font-bold text-blue-600" data-id="element-1077">
                        ${tour.price}
                      </span>
                      <span className="text-xs text-gray-500 ml-1" data-id="element-1078">
                        per person
                      </span>
                    </div>
                  </td>)}
                {[...Array(3 - selectedTours.length)].map((_, i) => <td key={i} className="py-3 px-4 bg-gray-50" data-id="element-1079"></td>)}
              </tr>
              {featureCategories.slice(2).map(category => renderFeatureComparison(category.id, category.label))}
              <tr data-id="element-1080">
                <td className="py-3 px-4 bg-gray-50 font-medium" data-id="element-1081">Actions</td>
                {selectedTours.map(tour => <td key={tour.id} className="py-3 px-4" data-id="element-1082">
                    <button className="px-3 py-1.5 bg-blue-600 text-white text-sm rounded-md hover:bg-blue-700 transition-colors flex items-center" onClick={() => handleViewDetails(tour.id)} data-id="element-1083">
                      View Details
                      <ArrowRightIcon className="h-4 w-4 ml-1" data-id="element-1084" />
                    </button>
                  </td>)}
                {[...Array(3 - selectedTours.length)].map((_, i) => <td key={i} className="py-3 px-4 bg-gray-50" data-id="element-1085"></td>)}
              </tr>
            </tbody>
          </table>
        </div>}
      {/* Tour selector modal */}
      {showTourSelector && <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4" data-id="element-1086">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl max-h-[80vh] flex flex-col" data-id="element-1087">
            <div className="p-4 border-b border-gray-200 flex justify-between items-center" data-id="element-1088">
              <h2 className="text-lg font-medium text-gray-800" data-id="element-1089">
                Select Tours to Compare
              </h2>
              <button className="text-gray-500 hover:text-gray-700" onClick={() => {
            setShowTourSelector(false);
            setSearchTerm('');
          }} data-id="element-1090">
                <XIcon className="h-5 w-5" data-id="element-1091" />
              </button>
            </div>
            <div className="p-4 border-b border-gray-200" data-id="element-1092">
              <div className="relative" data-id="element-1093">
                <input type="text" placeholder="Search tours by name or destination..." className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500" value={searchTerm} onChange={e => setSearchTerm(e.target.value)} data-id="element-1094" />
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none" data-id="element-1095">
                  <SearchIcon className="h-4 w-4 text-gray-400" data-id="element-1096" />
                </div>
              </div>
            </div>
            <div className="flex-1 overflow-auto p-4" data-id="element-1097">
              <div className="grid grid-cols-2 gap-4" data-id="element-1098">
                {filteredTours.map(tour => <div key={tour.id} className={`border rounded-lg overflow-hidden cursor-pointer transition-all ${selectedTours.some(t => t.id === tour.id) ? 'border-blue-500 ring-2 ring-blue-200' : 'border-gray-200 hover:border-blue-300'}`} onClick={() => addTourToComparison(tour)} data-id="element-1099">
                    <div className="flex h-24" data-id="element-1100">
                      <div className="w-1/3" data-id="element-1101">
                        <img src={tour.image} alt={tour.title} className="w-full h-full object-cover" data-id="element-1102" />
                      </div>
                      <div className="w-2/3 p-3" data-id="element-1103">
                        <h3 className="font-medium text-gray-800 line-clamp-1" data-id="element-1104">
                          {tour.title}
                        </h3>
                        <p className="text-xs text-gray-600" data-id="element-1105">
                          {tour.destination}
                        </p>
                        <div className="flex justify-between items-center mt-1" data-id="element-1106">
                          <span className="text-sm font-bold text-blue-600" data-id="element-1107">
                            ${tour.price}
                          </span>
                          <span className="text-xs text-gray-500" data-id="element-1108">
                            {tour.duration}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>)}
                {filteredTours.length === 0 && <div className="col-span-2 py-8 text-center text-gray-500" data-id="element-1109">
                    No tours found matching your search.
                  </div>}
              </div>
            </div>
            <div className="p-4 border-t border-gray-200 flex justify-between" data-id="element-1110">
              <span className="text-sm text-gray-600" data-id="element-1111">
                {selectedTours.length}/3 tours selected
              </span>
              <div data-id="element-1112">
                <button className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 mr-2 hover:bg-gray-50" onClick={() => {
              setShowTourSelector(false);
              setSearchTerm('');
            }} data-id="element-1113">
                  Cancel
                </button>
                <button className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700" onClick={() => {
              setShowTourSelector(false);
              setSearchTerm('');
            }} data-id="element-1114">
                  Done
                </button>
              </div>
            </div>
          </div>
        </div>}
    </div>;
};